/**
 * ConfirmDialog
 *
 * Shared yes/no confirmation modal. Two surfaces:
 *
 *   - Simple confirm — default behavior. Renders a title + message
 *     and two buttons. Used everywhere from "leave without saving" to
 *     "remove this enrollment".
 *
 *   - Typed confirm — pass `confirmText="DELETE"` (or any literal)
 *     and the dialog renders an input below the message that must
 *     match the literal exactly before the confirm button enables.
 *     Used as a soft safeguard on destructive actions where a single
 *     misclick on "Yes" would do real damage (e.g. deleting a course
 *     and its lessons).
 *
 * `confirmText` automatically renders the confirm button in error
 * color and defaults its label to "Delete" — both can be overridden
 * via `destructive={false}` / `confirmLabel` if a future caller wants
 * typed confirmation for a non-destructive action.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from './ui';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  content: string | React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  /** When provided, the user must type this exact (case-sensitive)
   *  string into a confirmation input before the confirm button
   *  enables. Typical use: `confirmText="DELETE"`. */
  confirmText?: string;
  /** Override the confirm button label. Defaults to "Delete" when
   *  `confirmText` or `destructive` is set, otherwise "Yes". */
  confirmLabel?: string;
  /** Override the cancel button label. Defaults to "Cancel" when
   *  `confirmText` or `destructive` is set, otherwise "No". */
  cancelLabel?: string;
  /** Renders the confirm button in error color and switches the
   *  default labels to Delete / Cancel. Auto-on when `confirmText`
   *  is set. */
  destructive?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  content,
  onConfirm,
  onCancel,
  confirmText,
  confirmLabel,
  cancelLabel,
  destructive,
}) => {
  const requiresTyping = !!confirmText;
  const isDestructive = !!destructive || requiresTyping;
  const [typed, setTyped] = useState('');

  // Reset the input each time the dialog reopens so stale text from a
  // previous open can't pre-arm the confirm button.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const matchesConfirm = !requiresTyping || typed === confirmText;
  const handleConfirmClick = () => {
    if (!matchesConfirm) return;
    onConfirm();
  };

  const resolvedConfirmLabel = confirmLabel ?? (isDestructive ? 'Delete' : 'Yes');
  const resolvedCancelLabel = cancelLabel ?? (isDestructive ? 'Cancel' : 'No');

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-description">{content}</DialogContentText>
        {requiresTyping && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Type <strong>{confirmText}</strong> to confirm.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              // Pressing Enter when the typed value matches submits —
              // matches the muscle-memory expectation on this kind of
              // safeguard. When the value doesn't match, Enter is a
              // no-op (the confirm button is disabled anyway).
              onKeyDown={(e) => {
                if (e.key === 'Enter' && matchesConfirm) handleConfirmClick();
              }}
              placeholder={confirmText}
              // Disable autofill / spellcheck so the browser doesn't
              // helpfully suggest the literal string and let users
              // pseudo-bypass the safeguard.
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: false,
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          {resolvedCancelLabel}
        </Button>
        <Button
          onClick={handleConfirmClick}
          color={isDestructive ? 'error' : 'primary'}
          variant="contained"
          disabled={requiresTyping && !matchesConfirm}
          // Don't autofocus the destructive button — that would let an
          // accidental Enter on dialog open fire the action before the
          // typed-confirm safeguard could engage.
          autoFocus={!isDestructive}
        >
          {resolvedConfirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
